import { useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { Button, HStack, Link, Stub, Text, VStack } from '@direct-frontend/components';
import { type TestProps } from '@direct-frontend/components/testing';
import {
    type BaseValidationError,
    useField,
    useFormContext,
    useFormInstance,
    useFormState,
} from '@adv-frontend/form-kit';
import { add, subtract } from '@adv-frontend/yastore-modules/lib';
import { useLoadingOrder } from '@/entities/order';
// eslint-disable-next-line @direct-frontend/modules/slice-boundaries -- Проблема в текущем файле, тк OrderForm должен быть в widgets
import { LoyaltyBlock } from '@/features/LoyaltyBlock';
import type { OrderDeliveryMethod, OrderDto, OrderPaymentMethod } from '@/shared/api/order';
import { OrderPaymentMethods } from '@/shared/api/order';
import { formatPrice } from '@/shared/lib/formatPrice';
import { nativePayEnabled } from '@/shared/lib/helpers';
import { pluralize } from '@/shared/lib/pluralize';
import { getRequestContext } from '@/shared/lib/requestContext';
import { Benefits } from './components/Benefits';
import { PaymentInformerCashback } from './components/PaymentInformerCashback';
import { PaymentInformerStickyButton } from './components/PaymentInformerStickyButton';
import { PaymentMethodSwitcher } from './components/PaymentMethodSwitcher';
import { PromoCodeInput } from './components/PromoCodeInput';
import { YandexPayBadge } from './components/YandexPayBadge';
import { orderForm } from './model/forms/orderForm';
import { OrderFormYandexPayWidget, type OrderPayboxSubmitParams } from './components';
import { ORDER_FORM_ID } from './constants';
import styles from './PaymentInformer.module.css';

const TERMS_OF_USE_LINK = 'https://yandex.ru/legal/merchantsiteconstruct_termsofuse/';

const ERRORS_MAPPING: Record<string, string> = {
    REQUIRED_LOCALITY: 'Необходимо заполнить город',
} as const;

const buildErrorMessage = (validationErrors: BaseValidationError[]): string | undefined => {
    const [validationError] = validationErrors;

    return validationError?.code ? ERRORS_MAPPING[validationError.code] : undefined;
};

interface SubmitPaymentButtonProps {
    isPending: boolean;
    handleSubmit: () => void;
    buttonText: string;
    loggedIn: boolean;
    submitErrorMessage?: string;
    paymentMethod: OrderPaymentMethod;
}

const SubmitPaymentButton = (props: SubmitPaymentButtonProps) => {
    const { isPending, handleSubmit, loggedIn, buttonText, paymentMethod, submitErrorMessage } = props;

    return (
        <VStack gap="4">
            <VStack gap="12" alignItems="center">
                <Button
                    size="m"
                    width="max"
                    color="accent"
                    isDisabled={isPending}
                    isLoading={isPending}
                    onClick={handleSubmit}
                >
                    {buttonText}
                </Button>
                {loggedIn && paymentMethod === OrderPaymentMethods.CARD_ONLINE && (
                    <HStack gap="8">
                        <Text size="m">Безопасная оплата с</Text>
                        <YandexPayBadge />
                    </HStack>
                )}
            </VStack>
            {submitErrorMessage && (
                <Text size="xs" color="red">
                    {submitErrorMessage}
                </Text>
            )}
        </VStack>
    );
};

interface PaymentInformerProps extends TestProps {
    className?: string;
    order: OrderDto;
    handlePayboxSubmit: (params: OrderPayboxSubmitParams) => void;
}

export const PaymentInformer = (props: PaymentInformerProps) => {
    const {
        account,
        isExpressCheckout,
        company: { companyShopUrl, legalBuyTermsLink },
        yandexPay: { merchantId, nativePayDisabled, yandexPayAvailableForAnonymous },
    } = getRequestContext();
    const { 'data-testid': dataTestId = 'PaymentInformer', className, order, handlePayboxSubmit } = props;

    const ref = useRef<HTMLDivElement>(null);
    const widgetRef = useRef<HTMLDivElement>(null);

    const [yaPayDiscount, setYaPayDiscount] = useState<undefined | string>();

    const formInstance = useFormInstance({ form: orderForm, id: ORDER_FORM_ID });
    const { fields } = formInstance;
    const context = useFormContext({ form: orderForm, id: ORDER_FORM_ID });
    const { errors, isSubmitting } = useFormState({ form: orderForm, id: ORDER_FORM_ID });

    const submitErrorMessage = buildErrorMessage(errors);
    const fieldDeliveryMethod = useField(fields.deliveryMethod);
    const fieldBonusesApplied = useField(fields.bonusesApplied);
    const fieldPaymentMethod = useField(fields.paymentMethod);

    const fullPrice = useMemo(
        () =>
            formatPrice(
                subtract(
                    add(order.total_final_price, order.delivery.final_customer_costs || 0),
                    fieldPaymentMethod.value === OrderPaymentMethods.CARD_ONLINE && yaPayDiscount ? yaPayDiscount : 0
                )
            ),
        [fieldPaymentMethod.value, order.delivery.final_customer_costs, order.total_final_price, yaPayDiscount]
    );

    const isFree = useMemo(
        () => (order.delivery.customer_costs ? parseFloat(order.delivery.customer_costs) === 0 : true),
        [order.delivery.customer_costs]
    );

    const isPending = isSubmitting || context.deliveryOptions.isLoading;

    const discount = useMemo(
        () =>
            add(
                subtract(order.total_final_price, order.total_price),
                subtract(order.delivery.final_customer_costs || 0, order.delivery.customer_costs || 0)
            ),
        [order.total_final_price, order.total_price, order.delivery.final_customer_costs, order.delivery.customer_costs]
    );

    const storeName = useMemo(
        () => context.order.store_legal_name || context.order.store_name || '',
        [context.order.store_legal_name, context.order.store_name]
    );
    const isNativePayEnabled = useMemo(
        () =>
            nativePayEnabled({
                yandexPayAvailableForAnonymous,
                nativePayDisabled,
                loggedIn: account.loggedIn,
                paymentMethod: fieldPaymentMethod.value,
            }),
        [account.loggedIn, fieldPaymentMethod.value, nativePayDisabled, yandexPayAvailableForAnonymous]
    );
    const legalUrl = useMemo(() => {
        if (isExpressCheckout) {
            return legalBuyTermsLink || companyShopUrl || TERMS_OF_USE_LINK;
        }

        return TERMS_OF_USE_LINK;
    }, [isExpressCheckout, companyShopUrl, legalBuyTermsLink]);

    const buttonText =
        fieldPaymentMethod.value === OrderPaymentMethods.CARD_ONLINE ? 'Перейти к оплате' : 'Оформить заказ';

    const buttonTextWithPrice = useMemo(() => {
        if (fieldPaymentMethod.value === OrderPaymentMethods.CARD_ONLINE) {
            return isPending ? buttonText : `${buttonText} ${fullPrice}`;
        }

        return buttonText;
    }, [buttonText, fieldPaymentMethod.value, fullPrice, isPending]);

    const totalDiscount = useMemo(
        () =>
            subtract(
                discount,
                yaPayDiscount && fieldPaymentMethod.value === OrderPaymentMethods.CARD_ONLINE ? yaPayDiscount : 0
            ),
        [discount, fieldPaymentMethod.value, yaPayDiscount]
    );

    const loyaltyBonuses = useMemo(() => {
        if (!order.loyalty_v2?.is_registered_user) {
            return undefined;
        }

        const { user_agreed_to_charge, total_discount_from_bonuses } = order.loyalty_v2.registered_user_loyalty;

        if (user_agreed_to_charge) {
            return total_discount_from_bonuses;
        }

        return undefined;
    }, [order.loyalty_v2]);

    const { isOrderSideUpdating } = useLoadingOrder();

    return (
        <VStack
            data-testid={dataTestId}
            ref={ref}
            justifyContent={isNativePayEnabled ? 'unset' : 'space-between'}
            className={cn(className, styles.root)}
        >
            <PaymentInformerStickyButton
                submitErrorMessage={submitErrorMessage}
                fullPrice={fullPrice}
                isPending={isPending}
                buttonTextWithPrice={buttonTextWithPrice}
                isNativePayEnabled={isNativePayEnabled}
                widgetRef={widgetRef}
                containerRef={ref}
            />

            <VStack gap="16">
                <PromoCodeInput orderStatus={order.status} />
                <VStack gap="8">
                    <VStack gap="4">
                        <HStack justifyContent="space-between">
                            <Text data-testid={`${dataTestId}.AvailableItemsQuantity`} size="s">
                                {pluralize(order.available_items_quantity, '%d товар', '%d товара', '%d товаров')}
                            </Text>
                            <Text size="s">{formatPrice(order.total_price)}</Text>
                        </HStack>
                        {totalDiscount < 0 && (
                            <Benefits
                                orderId={order.id}
                                discount={discount}
                                promocodeDiscount={order.promocode?.discount}
                                giftCardDiscount={order.gift_card?.discount}
                                yaPayDiscount={
                                    fieldPaymentMethod.value === OrderPaymentMethods.CARD_ONLINE
                                        ? yaPayDiscount
                                        : undefined
                                }
                                giftCardBalance={order.gift_card?.balance}
                                promocodeTitle={order.promocode?.code}
                                bonuses={loyaltyBonuses}
                            />
                        )}
                        {order.delivery.customer_costs && (
                            <HStack justifyContent="space-between">
                                <Text size="s">Доставка</Text>
                                {context.deliveryOptions.isLoading || isOrderSideUpdating ? (
                                    <Stub height={16} width={50} />
                                ) : (
                                    <Text size="s" color={isFree ? 'green' : 'gray'}>
                                        {isFree ? 'Бесплатно' : formatPrice(order.delivery.customer_costs)}
                                    </Text>
                                )}
                            </HStack>
                        )}

                        <PaymentInformerCashback loyalty={order.loyalty_v2} />
                    </VStack>
                    <HStack justifyContent="space-between">
                        <Text size="l" bold>
                            Итого
                        </Text>
                        {context.deliveryOptions.isLoading || isOrderSideUpdating ? (
                            <Stub height={28} width={100} />
                        ) : (
                            <Text size="l" bold>
                                {fullPrice}
                            </Text>
                        )}
                    </HStack>
                </VStack>

                <LoyaltyBlock
                    loyalty={order.loyalty_v2}
                    refetch={context.refetch}
                    isSelected={fieldBonusesApplied.value}
                    onChange={fieldBonusesApplied.getProps().onChange}
                />

                {Boolean(fieldDeliveryMethod.value) && (
                    <PaymentMethodSwitcher
                        {...fieldPaymentMethod.getProps()}
                        paymentMethods={context.paymentMethods.items}
                        paymentMethod={fieldPaymentMethod.value}
                        deliveryMethod={fieldDeliveryMethod.value as OrderDeliveryMethod}
                        isDeliveryLoading={context.deliveryOptions.isLoading}
                        isPaymentLoading={context.paymentMethods.isLoading}
                        isGiftCardApply={Boolean(order.gift_card)}
                        price={order.total_final_price}
                    />
                )}

                {!isNativePayEnabled && (
                    <SubmitPaymentButton
                        buttonText={buttonTextWithPrice}
                        isPending={isPending}
                        loggedIn={account.loggedIn}
                        handleSubmit={formInstance.submit}
                        submitErrorMessage={submitErrorMessage}
                        paymentMethod={fieldPaymentMethod.value}
                    />
                )}
                <OrderFormYandexPayWidget
                    ref={widgetRef}
                    paymentMethods="card,split"
                    nativePayEnabled={isNativePayEnabled}
                    orderId={order.id}
                    storeName={storeName}
                    legalUrl={legalUrl}
                    amount={add(order.total_final_price, order.delivery.final_customer_costs || 0)}
                    merchantId={merchantId}
                    loggedIn={account.loggedIn}
                    paymentMethod={fieldPaymentMethod.value}
                    onSubmit={handlePayboxSubmit}
                    setYaPayDiscount={setYaPayDiscount}
                    fallback={
                        <SubmitPaymentButton
                            buttonText={buttonTextWithPrice}
                            isPending={isPending}
                            loggedIn={account.loggedIn}
                            handleSubmit={formInstance.submit}
                            submitErrorMessage={submitErrorMessage}
                            paymentMethod={fieldPaymentMethod.value}
                        />
                    }
                />
            </VStack>
            {!isNativePayEnabled &&
                (isExpressCheckout ? (
                    <span className={styles.copyright}>
                        Нажимая «{buttonText}», вы соглашаетесь с&nbsp;
                        <Link
                            color="underline"
                            className={styles.copyright}
                            href={legalBuyTermsLink || companyShopUrl}
                            target="_blank"
                        >
                            условиями&#160;покупки
                        </Link>
                        &nbsp;продавца {storeName ? `«${storeName}»` : ''}
                    </span>
                ) : (
                    <span className={styles.copyright}>
                        Нажимая «{buttonText}», вы соглашаетесь с&nbsp;
                        <Link color="underline" className={styles.copyright} href={TERMS_OF_USE_LINK} target="_blank">
                            условиями&#160;использования
                        </Link>
                        &nbsp;сервиса и&nbsp;
                        <Link
                            color="underline"
                            className={styles.copyright}
                            href={`${companyShopUrl}/privacy`}
                            target="_blank"
                        >
                            пользовательским соглашением
                        </Link>
                    </span>
                ))}
        </VStack>
    );
};
